import { SigningRequest } from '@microsoft/esign.gateway.service.client';
import { Constants } from '../../../common/Constants';
import { ApprovalRequestType } from '../../../data/types.generated';
import { LogCategory } from '../../../logger';
import {
    ApprovalCreator,
    CreateAdaptiveCardClient,
    CreateAdaptiveCardControllerInput,
    DeleteAdaptiveCardClient,
    DeleteAdaptiveCardControllerInput,
    SignApprovalType,
} from '../../../service/gen';
import {
    CreateAdaptiveCardAdobeControllerInput,
    CreateAdaptiveCardClient as CreateAdaptiveCardAdobeClient,
    CreateEnvelopeInput,
    DeleteAdaptiveCardClient as DeleteAdaptiveCardAdobeClient,
} from '../../../service/gen/esign';
import { mapCreateEnvelopeInputToSigningRequest } from '../../../utilities/MapESignModel';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { delay } from '../../../utilities/Utils';
import { createEsignApproval } from '../CreateEsignApproval/CreateEsignApproval';
import { retrieveAdobeRequestDefaults } from '../utils/Authentication/AdobeAuthHelpers';

export const createAdobeApproval = async (
    envelopeInput: CreateEnvelopeInput,
    setEnvelopeUrl: (value: string) => void,
    setLoadingPage: (value: boolean) => void,
    setLoadingPageMessage: (message: string) => void,
    setErrorPage: (value: boolean) => void,
    setErrorPageMessage: (value: string) => void
): Promise<void> => {
    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();

    if (setLoadingPage) {
        setLoadingPageMessage(loc.strings.approval_create_loading_text);
        setLoadingPage(true);
    }

    const adobeRequestDefaults = await retrieveAdobeRequestDefaults();
    envelopeInput.EsignToken = adobeRequestDefaults.token;
    envelopeInput.ApiEndpoint = adobeRequestDefaults.apiAccessPoint;

    logger.logTraceInfo(
        LogCategory.CreateEsignApprovalView,
        `Calling Esign Approvals create endpoint from ${envelopeInput.Creator}`
    );

    // If created from chat, send the adaptive card first to get the message id.
    if (
        envelopeInput.WebhookBaseDataObject != null &&
        envelopeInput.WebhookBaseDataObject.ApprovalCreator !== ApprovalCreator.PersonalApp
    ) {
        if (host.getEcsConfig().MicrosoftTeamApprovals.EsignTeamsEndpointMigrationEnabled) {
            const cardClient = new CreateAdaptiveCardClient();
            const adaptiveCardInput: CreateAdaptiveCardControllerInput = {
                SignApprovalType: SignApprovalType.Adobe,
                UserName: envelopeInput.UserName,
                UserEmail: host.getUserPrincipalName(),
                ClientRequestId: envelopeInput.ClientRequestId,
                WebhookBaseDataObject: envelopeInput.WebhookBaseDataObject,
            };
            envelopeInput.Creator = envelopeInput.WebhookBaseDataObject.ApprovalCreator;
            const adaptiveCardOutput = await cardClient.createAdaptiveCard(adaptiveCardInput);
            envelopeInput.WebhookBaseDataObject.MessageId = adaptiveCardOutput.MessageId;
        } else {
            const cardClient = new CreateAdaptiveCardAdobeClient();
            const adaptiveCardInput: CreateAdaptiveCardAdobeControllerInput = {
                EnvelopeData: {
                    ...envelopeInput,
                    UserEmail: host.getUserPrincipalName(),
                },
            };
            envelopeInput.Creator = envelopeInput.WebhookBaseDataObject.ApprovalCreator;
            const adaptiveCardOutput = await cardClient.createAdaptiveCard(adaptiveCardInput);
            envelopeInput.WebhookBaseDataObject.MessageId = adaptiveCardOutput.MessageId;
        }
    }

    try {
        const signingRequestUrl = await createEsignApproval(
            ApprovalRequestType.ESign,
            (): SigningRequest => mapCreateEnvelopeInputToSigningRequest(envelopeInput)
        );
        setEnvelopeUrl(signingRequestUrl);
        await delay(Constants.screenReaderWaitTime);
        setLoadingPageMessage(loc.strings.approval_create_success_text);
    } catch (error: any) {
        if (envelopeInput.Creator !== ApprovalCreator.PersonalApp) {
            // Clean up the adaptive card, if any.
            if (envelopeInput.WebhookBaseDataObject.MessageId != null) {
                if (host.getEcsConfig().MicrosoftTeamApprovals.EsignTeamsEndpointMigrationEnabled) {
                    const cardClient = new DeleteAdaptiveCardClient();
                    const input: DeleteAdaptiveCardControllerInput = {
                        SignApprovalType: SignApprovalType.Adobe,
                        WebhookBaseDataObject: envelopeInput.WebhookBaseDataObject,
                    };
                    cardClient.deleteAdaptiveCard(input);
                } else {
                    const cardClient = new DeleteAdaptiveCardAdobeClient();
                    cardClient.deleteAdaptiveCard(envelopeInput.WebhookBaseDataObject);
                }
            }
        }

        const errorCode = error?.message;

        if (setErrorPage) {
            setErrorPage(true);
            let userErrorMessage = loc.strings.error_default;
            if (errorCode === 'ApproverRoleNotEnabled'.toLowerCase()) {
                userErrorMessage = loc.strings.esign_approver_role_not_enabled;
            } else if (errorCode === 'UnauthorizedToSendDocuments'.toLowerCase()) {
                userErrorMessage = loc.strings.esign_unauthorized_to_send_documents;
            } else if (errorCode === 'PermissionDenied'.toLowerCase()) {
                userErrorMessage = loc.strings.esign_permission_denied;
            } else if (errorCode === 'PhoneSecurityOptionMissing'.toLowerCase()) {
                userErrorMessage = loc.strings.esign_phone_security_option_missing;
            } else if (errorCode === 'AgreementCreationQuotaExausted'.toLowerCase()) {
                userErrorMessage = loc.strings.esign_agreement_creation_quota_exausted;
            } else if (errorCode === 'ThrottlingSendLimitsReachedForTimeBucket'.toLowerCase()) {
                userErrorMessage = loc.strings.esign_throttling_send_limits_reached_for_time_bucket;
            }
            setErrorPageMessage(userErrorMessage);
        }
    } finally {
        if (setLoadingPage) {
            setLoadingPage(false);
        }
    }
};
